/* eslint-disable no-console */
/* eslint-disable max-lines */
import { useEffect, useState } from 'react'

import { CURRENT_USER_SERVLET, POWERPORTAL_USER_PROFILE_SERVLET } from './userServletConstants'

type CurrentUser = {
    name?: string
    home?: string
    displayName?: string
    givenName?: string
    familyName?: string
    email?: string
    authorizableId_xss?: string
    [key: string]: unknown
}

type PowerPortalProfile = {
    displayName?: string
    email?: string
    givenName?: string
    samlAttributes?: {
        authMethods?: string
        city?: string
        company?: string
        countryCode?: string
        countryName?: string
        displayName?: string
        email?: string
        emailAddress?: string
        givenName?: string
        groups?: string
        identityProvider?: string
        objectId?: string
        partnerProgramLevel?: string
        partnerProgramPreference?: string
        partnerProgramProfile?: string
        phone?: string
        postalCode?: string
        streetAddress?: string
        surname?: string
        tenantId?: string
        userId?: string
        [key: string]: unknown
    }
    [key: string]: unknown
}

type UserDetails = {
    currentUser: CurrentUser | null
    powerPortalProfile: PowerPortalProfile | null
}

type UseUserInfoReturn = {
    userInfo: CurrentUser | null
    userLoggedInDetails: PowerPortalProfile | null
    isLoading: boolean
    userDetails: UserDetails
}
const fetchCurrentUser = async (): Promise<CurrentUser | null> => {
    const ms = Date.now()
    const url = `${CURRENT_USER_SERVLET}?currentms=${ms}`
    const response = await fetch(url)

    if (!response.ok) {
        throw new Error('Failed to fetch user info')
    }
    return (await response.json()) as CurrentUser
}

const fetchPowerPortalProfile = async (userKey: string): Promise<PowerPortalProfile | null> => {
    const url = `${POWERPORTAL_USER_PROFILE_SERVLET}?userKey=${encodeURIComponent(userKey)}`
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error('Failed to fetch powerportal user profile')
    }
    return (await response.json()) as PowerPortalProfile
}

export const useUserInfo = (): UseUserInfoReturn => {
    const [userInfo, setUserInfo] = useState<CurrentUser | null>(null)
    const [userLoggedInDetails, setUserLoggedInDetails] = useState<PowerPortalProfile | null>(null)
    const [isLoadingState, setIsLoadingState] = useState(true)

    useEffect(() => {
        let isMounted = true

        const fetchUser = async () => {
            setIsLoadingState(true)
            try {
                const currentUser = await fetchCurrentUser()
                if (!isMounted) {
                    return
                }
                setUserInfo(currentUser)

                const userKey = currentUser?.authorizableId_xss

                if (typeof userKey === 'string' && userKey.length > 0 && userKey !== 'anonymous') {
                    try {
                        const profile = await fetchPowerPortalProfile(userKey)
                        if (isMounted) {
                            setUserLoggedInDetails(profile)
                        }
                    } catch (profileError) {
                        if (isMounted) {
                            setUserLoggedInDetails(null)
                        }
                    }
                } else {

                    setUserLoggedInDetails(null)
                }
            } catch (userError) {
                console.error('Failed to fetch current user:', userError)
                if (isMounted) {
                    setUserInfo(null)
                    setUserLoggedInDetails(null)
                }
            }
            if (isMounted) {
                setIsLoadingState(false)
            }
        }

        fetchUser()

        return () => {
            isMounted = false
        }
    }, [])

    const userDetails: UserDetails = {
        currentUser: userInfo,
        powerPortalProfile: userLoggedInDetails,
    }

    return {
        userInfo,
        userLoggedInDetails,
        isLoading: isLoadingState,
        userDetails,
    }
}